import { createSlice, createSelector } from '@reduxjs/toolkit';
import { coreApi } from 'api/core';
import { setShowMessage } from "redux/slices/uiSlice";

export const APP_NAMESPACE = 'mkrn-starter';
const userPrefix = `${APP_NAMESPACE}/user`;
const usersPrefix = `${APP_NAMESPACE}/users`;
const authUserPrefix = `${APP_NAMESPACE}/auth`;

const initialState = {
  messages: {
    [userPrefix]: "",
    [usersPrefix]: "",
  },
  errors: {
    [userPrefix]: [],
    [usersPrefix]: [],
    [authUserPrefix]: [],
  },
  loading: {
    [userPrefix]: false,
    [usersPrefix]: false,
    [authUserPrefix]: false,
  },
  user: null, // logged-in user
  selectedUser: null, // for getUserById
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
  },
});

export const { setUser, setUsers, setSelectedUser, setUserSettings } = userSlice.actions;
export default userSlice.reducer;

export const fetchUserInfo = () => async (dispatch) => {
  try {
    const user = await coreApi.fetch('/users/me');
    dispatch(setUser(user));
  } catch (err) {
  }
};

export const logout = () => async (dispatch) => {
  dispatch(setUser(null));
  localStorage.removeItem('token');

  return window.location.href = '/login';
};

const userSelector = (state) => state.newUser.user;

export const selectUser = createSelector(userSelector, (user) => user);

export const isAdminUserSelector = createSelector(userSelector, (user) => {
  // return user?.email.startsWith('admin@')
  return !!(user?.permissions?.customers?.admin);
});
export const isAdminSelector = createSelector(userSelector, (user) => {
  return !!(user?.permissions?.admin || user?.permissions?.user);
});
export const isSuperAdminUserSelector = createSelector(userSelector, (user) => {
  return user?.isSuperAdmin === true;
});

export const getAllUsers = () => async (dispatch) => {
  try {
    const users = await coreApi.fetch('/users', 'GET');
    dispatch(setUsers(users));
    return users;
  } catch (err) {
    dispatch(setShowMessage({
      description: "Failed to fetch users",
      type: "error",
    }));
    throw err;
  }
};

export const getUserById = (id) => async (dispatch) => {
  try {
    const user = await coreApi.fetch(`/users/${id}`, 'GET');
    dispatch(setSelectedUser(user));
    return user;
  } catch (err) {
    dispatch(setShowMessage({
      description: "Failed to fetch user",
      type: "error",
    }));
    throw err;
  }
};

export const createUser = (payload) => async (dispatch) => {
  try {
    const user = await coreApi.post('/users', payload);
    dispatch(setShowMessage({
      description: "User created successfully",
      type: "success",
    }));
    return user;
  } catch (err) {
    if (err.response?.status === 409) {
      dispatch(setShowMessage({
        description: "User already exists",
        type: "error",
      }));
    } else {
      dispatch(setShowMessage({
        description: "Failed to create user",
        type: "error",
      }));
    }
    throw err;
  }
};

export const updateUser = (id, payload) => async (dispatch) => {
  try {
    const user = await coreApi.put(`/users/${id}`, payload);
    dispatch(setShowMessage({
      description: "User updated successfully",
      type: "success",
    }));
    return user;
  } catch (err) {
    if (err.response?.status === 409) {
      dispatch(setShowMessage({
        description: "User already exists",
        type: "error",
      }));
    } else {
      dispatch(setShowMessage({
        description: "Failed to update user",
        type: "error",
      }));
    }
    throw err;
  }
};

const usersSelector = (state) => state.newUser.users || [];
export const selectUsers = createSelector(usersSelector, (users) => users);

const usersStatusSelector = (state) => state.newUser.loading[usersPrefix] || false;
export const selectUsersStatus = createSelector(usersStatusSelector, (loading) => loading);

// Selector for selectedUser (user details)
const selectedUserSelector = (state) => state.newUser.selectedUser;
export const selectSelectedUser = createSelector(selectedUserSelector, (user) => user);